import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';

type View = 'login' | 'register' | 'verify' | 'forgot' | 'reset';

/**
 * Everything before a session exists.
 *
 * One component rather than five, because the steps hand off to each other:
 * registering leads to verifying, forgetting leads to resetting, and every one
 * of them can end at signing in. Splitting them would mean lifting the email
 * between them anyway.
 */
export function AuthScreen({ onSignedIn, unclaimed = false }: { onSignedIn: () => void; unclaimed?: boolean }) {
  // An invite link arrives as ?invite=CODE&email=..., so it opens on register
  // with both filled in. Nobody should have to retype a code they were sent.
  const params = new URLSearchParams(window.location.search);
  const invited = params.get('invite');
  const [view, setView] = useState<View>(invited || unclaimed ? 'register' : 'login');
  const [invite, setInvite] = useState(invited ?? '');


  const [email, setEmail] = useState(params.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [company, setCompany] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const code = invite.trim();
    if (!code || !email.includes('@')) {
      setInvitation(null);
      return;
    }
    let cancelled = false;
    api
      .inviteDetails(email, code)
      .then((d) => !cancelled && setInvitation(d))
      // A bad or expired code is reported when they submit, with the wording
      // the server chooses. Nothing useful to say here yet.
      .catch(() => !cancelled && setInvitation(null));
    return () => {
      cancelled = true;
    };
  }, [invite, email]);
  // What the invitation is for. Looked up rather than guessed, so the screen
  // can name the company instead of saying "an existing company".
  const [invitation, setInvitation] = useState<
    { company: string; role: string; expiresAt: string; vehicles: string[] } | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (view === 'login') {
      return run(async () => {
        const r = await api.login({ email, password });
        // Signing in unverified lands on the code step rather than the app.
        if (!r.verified) {
          setView('verify');
          setNotice('Enter the code sent when you registered, or ask for a new one.');
          return;
        }
        onSignedIn();
      });
    }

    if (view === 'register') {
      return run(async () => {
        const r = await api.register({ email, password, firstName, lastName, username, ...(invite.trim() ? { invite: invite.trim() } : { company }) });

        // An invite already proved the address - the code reached that inbox -
        // so the server accepts it, marks it verified and opens a session in
        // one step. Sending them to the verification screen asked for a
        // six-digit code that was never issued and does not exist.
        if (r.verified) {
          onSignedIn();
          return;
        }

        setView('verify');
        // When mail could not be sent the code is in the server log; saying so
        // beats a silent wait for something that will never arrive.
        setNotice(r.notice ?? `A six-digit code is on its way to ${r.email}.`);
      });
    }

    if (view === 'verify') {
      return run(async () => {
        await api.verifyEmail({ email, code });
        onSignedIn();
      });
    }

    if (view === 'forgot') {
      return run(async () => {
        const r = await api.forgotPassword(email);
        setView('reset');
        setNotice(r.notice);
      });
    }

    return run(async () => {
      const r = await api.resetPassword({ email, code, password });
      setView('login');
      setPassword('');
      setCode('');
      setNotice(r.notice);
    });
  };

  const heading = {
    login: 'Sign in',
    register: 'Create an account',
    verify: 'Check your email',
    forgot: 'Reset your password',
    reset: 'Choose a new password',
  }[view];

  return (
    <div className="auth-shell">
      <form className="card auth-card" onSubmit={submit}>
        <span className="wordmark-lg">Concourse Works</span>
        <h1>{heading}</h1>

        {view === 'verify' && (
          <p className="small dim">
            We sent a six-digit code to <strong>{email}</strong>. It expires in fifteen minutes.
          </p>
        )}

        {notice && <div className="banner info small">{notice}</div>}
        {error && <div className="banner error small">{error}</div>}

        {view !== 'verify' && view !== 'reset' && (
          <label className="field">
            <span>Email</span>
            <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
        )}

        {view === 'register' && unclaimed && !invite && (
          <p className="small dim">
            Nobody has claimed this instance yet. The first account owns it.
          </p>
        )}

        {/* Names what they are accepting. Somebody arriving from a link had no
            idea which company invited them or whether the code in the email had
            been used - the form never asked for it, which reads as a step
            skipped rather than one already done. */}
        {view === 'register' && invitation && (
          <div className="invite-summary">
            <strong>{invitation.company}</strong> has invited you
            {invitation.role === 'customer' ? '' : ` as a ${invitation.role}`}
            {invitation.vehicles.length > 0 && (
              <> to see {invitation.vehicles.join(' and ')}</>
            )}
            .
            <span className="small dim">
              {invited
                ? 'Your code is filled in below. Choose a password and you are in.'
                : 'That code checks out. Choose a password below.'}
            </span>
          </div>
        )}

        {view === 'register' && invite && !invitation && (
          <p className="small dim">
            You are joining an existing company. Choose a password and you are in.
          </p>
        )}

        {/* The invite email prints the code as well as linking, for clients
            that mangle a URL. Until now there was nowhere to type it, so that
            fallback led to a form asking for a company name instead. */}
        {view === 'register' && (
          <label className="field">
            <span>Invite code</span>
            <input
              type="text"
              className="mono"
              autoComplete="off"
              placeholder="ABCD-EFGH-IJKL"
              value={invite}
              // Shown filled and locked when it arrived in the link. Telling
              // somebody their code was applied is weaker than showing it to
              // them: they can hold it against the one in the email and see
              // for themselves that the right invitation was picked up.
              readOnly={Boolean(invited)}
              aria-readonly={Boolean(invited)}
              onChange={(e) => setInvite(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
            />
            <span className="small dim">
              {invited
                ? 'Taken from your invitation link. It should match the code in your email.'
                : 'Optional. Enter the code from your invitation to join an existing company.'}
            </span>
          </label>
        )}

        {view === 'register' && (
          <>
            <div className="row wrap" style={{ gap: 8 }}>
              <label className="field" style={{ flex: '1 1 120px' }}>
                <span>First name</span>
                <input type="text" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </label>
              <label className="field" style={{ flex: '1 1 120px' }}>
                <span>Last name</span>
                <input type="text" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </label>
            </div>
            <label className="field">
              <span>Username</span>
              <input
                type="text" autoComplete="nickname" value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
              />
              <span className="small dim">Optional. Shown instead of your name when set.</span>
            </label>
            <label className="field" style={{ display: invite.trim() ? 'none' : undefined }}>
              <span>Company</span>
              <input type="text" required={!invite.trim()} value={company} onChange={(e) => setCompany(e.target.value)} />
              <span className="small dim">Your vehicles and scans belong to this company, not to you personally.</span>
            </label>
          </>
        )}

        {(view === 'verify' || view === 'reset') && (
          <label className="field">
            <span>Six-digit code</span>
            <input
              className="mono"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </label>
        )}

        {view !== 'verify' && view !== 'forgot' && (
          <label className="field">
            <span>{view === 'reset' ? 'New password' : 'Password'}</span>
            <input
              type="password"
              autoComplete={view === 'login' ? 'current-password' : 'new-password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {view !== 'login' && (
              <span className="small dim">At least 12 characters. A short phrase beats a short password.</span>
            )}
          </label>
        )}

        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Working…' : heading}
        </button>

        <div className="auth-links">
          {view === 'login' && (
            <>
              <button type="button" className="linkish" onClick={() => { setView('register'); setNotice(null); setError(null); }}>
                Create an account
              </button>
              <button type="button" className="linkish" onClick={() => { setView('forgot'); setNotice(null); setError(null); }}>
                Forgot your password
              </button>
            </>
          )}
          {view === 'register' && (
            <button type="button" className="linkish" onClick={() => { setView('login'); setNotice(null); setError(null); }}>
              I already have an account
            </button>
          )}
          {view === 'verify' && (
            <button
              type="button"
              className="linkish"
              disabled={busy}
              onClick={() => run(async () => {
                const r = await api.resendCode(email);
                setNotice(r.notice ?? 'A new code is on its way.');
              })}
            >
              Send another code
            </button>
          )}
          {(view === 'forgot' || view === 'reset') && (
            <button type="button" className="linkish" onClick={() => { setView('login'); setNotice(null); setError(null); }}>
              Back to sign in
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
